/**
 * AmcPoolPanel — POAGG-AMC-001 v2 Phase 2 (2026-02-15)
 *
 * Surfaces the PO's AMC Pool with six tiles, project-completion metadata,
 * linked AMC schedules, and the two CTAs ("Record Project Completion",
 * "Activate AMC"). Embeds the two dialogs that drive the lifecycle.
 *
 * Renders only when the parent PO carries AMC value (bs.has_amc_value).
 *
 * Data shape (from /api/billing/po/{po_id} billing_status v2):
 *   - amc_po_total, amc_po_excl_gst             (Contract — read-only)
 *   - amc_allocated_amount/_excl_gst             (Σ schedule.total_amount)
 *   - amc_available_to_allocate_amount/_excl_gst (Contract − Allocated)
 *   - amc_invoiced_amount/_excl_gst              (Σ cycle invoices)
 *   - amc_outstanding_amount/_excl_gst           (Allocated − Invoiced)
 *   - linked_amc_schedule_count
 *   - linked_amc_schedules[]
 *   - project_completion_date, _remarks, _recorded_by, _recorded_at, _recorded
 *   - amc_activation_unlocked, amc_activation_gate_reasons[]
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    Calendar, CheckCircle, ChartLineUp, Lightning, Plus, Receipt, Shield,
    WarningCircle, ArrowRight, ListChecks,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { formatINR, formatDate } from "../../lib/format";
import { annualPeriodEndIso } from "../../lib/servicePeriod";
import { useAuth } from "../../contexts/AuthContext";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import FormDialog, { FormDialogCancel } from "../FormDialog";
import StatusBadge from "../StatusBadge";

const ALLOWED_FREQUENCIES = ["Annual", "Half-Yearly", "Quarterly", "Monthly"];
const ALLOWED_GST_RATES = [0, 5, 12, 18, 28];

export function PoolTile({ label, value, currency, tone = "slate", testid, subtitle }) {
    const toneText = {
        slate: "text-slate-900",
        indigo: "text-indigo-700",
        emerald: "text-emerald-700",
        amber: "text-amber-700",
        rose: "text-rose-700",
        violet: "text-violet-700",
    }[tone];
    return (
        <div
            className="rounded-md border border-slate-200 bg-white px-3 py-2.5 flex flex-col gap-0.5"
            data-testid={testid}
        >
            <div className="reedos-eyebrow text-[9px] leading-tight">{label}</div>
            <div className={`reedos-data font-display font-bold text-base ${toneText}`}>
                {typeof value === "number" ? formatINR(value, currency) : value}
            </div>
            {subtitle && <div className="text-[10px] text-slate-500 leading-tight">{subtitle}</div>}
        </div>
    );
}

export function CountTile({ label, count, testid }) {
    return (
        <div
            className="rounded-md border border-slate-200 bg-white px-3 py-2.5 flex flex-col gap-0.5"
            data-testid={testid}
        >
            <div className="reedos-eyebrow text-[9px] leading-tight">{label}</div>
            <div className="reedos-data font-display font-bold text-base text-violet-700">{count}</div>
            <div className="text-[10px] text-slate-500 leading-tight">linked schedule{count === 1 ? "" : "s"}</div>
        </div>
    );
}

/* ------------------------------------------------------------------------ */
/*  RecordProjectCompletionDialog                                          */
/* ------------------------------------------------------------------------ */

function RecordProjectCompletionDialog({ po, onSaved }) {
    const { user } = useAuth();
    const [open, setOpen] = useState(false);
    const isAmendment = !!po?.billing_status?.project_completion_date;
    const canOpen = isAmendment ? user?.role === "super_admin" : true;

    return (
        <>
            <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={!canOpen}
                onClick={() => setOpen(true)}
                data-testid="record-project-completion-btn"
                title={!canOpen ? "Only Super Admin can amend a recorded date" : undefined}
            >
                <Calendar size={14} className="mr-1.5" />
                {isAmendment ? "Amend Project Completion" : "Record Project Completion"}
            </Button>
            {open && (
                <RecordProjectCompletionForm
                    po={po}
                    isAmendment={isAmendment}
                    open={open}
                    onOpenChange={setOpen}
                    onSaved={onSaved}
                />
            )}
        </>
    );
}

function RecordProjectCompletionForm({ po, isAmendment, open, onOpenChange, onSaved }) {
    const initialDate = po?.billing_status?.project_completion_date || new Date().toISOString().slice(0, 10);
    const initialRemarks = po?.billing_status?.project_completion_remarks || "";
    const [date, setDate] = useState(initialDate);
    const [remarks, setRemarks] = useState(initialRemarks);
    const [busy, setBusy] = useState(false);

    const isDirty = date !== initialDate || remarks !== initialRemarks;

    const submit = async (e) => {
        e?.preventDefault?.();
        if (!date) { toast.error("Project completion date is required"); return; }
        if (!remarks || remarks.trim().length < 1) {
            toast.error("Remarks are required");
            return;
        }
        setBusy(true);
        try {
            await api.post(
                `/billing/po/${po.id}/record-project-completion`,
                { date, remarks: remarks.trim() },
            );
            toast.success(isAmendment ? "Project completion amended" : "Project completion recorded");
            onOpenChange(false);
            onSaved?.();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    return (
        <FormDialog
            open={open}
            onOpenChange={onOpenChange}
            isDirty={isDirty}
            contentClassName="max-w-md"
            testId="record-pcd-dialog"
            title={isAmendment ? "Amend Project Completion Date" : "Record Project Completion"}
            description={
                isAmendment
                    ? "Super Admin only. The previous record is preserved in the audit chain."
                    : "Records that implementation is delivered, UAT is signed off, and the project has moved into the support phase. This unlocks AMC activation."
            }
        >
            <form onSubmit={submit} className="space-y-4 pt-2">
                <div className="space-y-1.5">
                    <Label htmlFor="pcd-date">Project Completion Date</Label>
                    <Input
                        id="pcd-date" type="date"
                        value={date} onChange={(e) => setDate(e.target.value)}
                        max={new Date().toISOString().slice(0, 10)}
                        data-testid="pcd-date-input"
                        required
                    />
                    <p className="text-[11px] text-slate-500">Cannot be in the future.</p>
                </div>
                <div className="space-y-1.5">
                    <Label htmlFor="pcd-remarks">Remarks</Label>
                    <Textarea
                        id="pcd-remarks" rows={4}
                        value={remarks} onChange={(e) => setRemarks(e.target.value)}
                        placeholder="UAT signed off on …; handover completed on …; client confirmed."
                        data-testid="pcd-remarks-input"
                        required maxLength={2000}
                    />
                    <p className="text-[11px] text-slate-500">{remarks.length}/2000 characters</p>
                </div>
                <div className="flex justify-end gap-2 pt-1">
                    <FormDialogCancel testId="pcd-cancel-btn" />
                    <Button type="submit" disabled={busy} data-testid="pcd-submit-btn">
                        {busy ? "Saving…" : (isAmendment ? "Save Amendment" : "Record Completion")}
                    </Button>
                </div>
            </form>
        </FormDialog>
    );
}

/* ------------------------------------------------------------------------ */
/*  CreateAmcFromPoDialog                                                  */
/* ------------------------------------------------------------------------ */

export function CreateAmcFromPoDialog({ po, preview, onCreated, disabled, disabledReason, kind = "amc" }) {
    const { user } = useAuth();
    const isSuperAdmin = user?.role === "super_admin";
    const [open, setOpen] = useState(false);

    return (
        <>
            <Button
                type="button"
                disabled={disabled || !isSuperAdmin}
                onClick={() => setOpen(true)}
                data-testid={kind === "subscription" ? "activate-subscription-btn" : "activate-amc-btn"}
                title={
                    !isSuperAdmin
                        ? "Schedule activation is restricted to Super Admin"
                        : disabledReason || undefined
                }
            >
                <Lightning size={14} className="mr-1.5" weight="fill" />
                {kind === "subscription" ? "Activate Subscription" : "Activate AMC"}
            </Button>
            {open && (
                <CreateAmcFromPoForm
                    po={po}
                    preview={preview}
                    open={open}
                    onOpenChange={setOpen}
                    onCreated={onCreated}
                    kind={kind}
                />
            )}
        </>
    );
}

function CreateAmcFromPoForm({ po, preview, open, onOpenChange, onCreated, kind = "amc" }) {
    const isSub = kind === "subscription";
    const pool = preview?.pool_summary || {};
    const pcGate = preview?.project_completion_gate || {};
    const implGate = preview?.implementation_gate || {};
    const defaultGstPct = preview?.amc_contract?.gst_percent ?? 18;
    const suggestedFrequencies = preview?.frequency_resolution?.operator_selectable?.length
        ? preview.frequency_resolution.operator_selectable
        : ALLOWED_FREQUENCIES;
    const autoDerivedFreq = preview?.frequency_resolution?.auto_derived || "Annual";

    const [form, setForm] = useState({
        amc_name: `${isSub ? "Annual Cost" : "AMC"} — ${po?.client?.name || "Schedule"}`,
        amount_excl_gst: "",
        gst_percent: String(defaultGstPct ?? 18),
        start_date: preview?.suggested_start_date || "",
        end_date: preview?.suggested_end_date || "",
        billing_frequency: autoDerivedFreq,
        billing_day: "",
        notes: "",
        project_completion_override_note: "",
        early_activation_override_note: "",
        over_allocation_override_note: "",
        duplicate_override_note: "",
    });
    const [busy, setBusy] = useState(false);
    // DEDUP-AMC-001 (2026-02-15) — Debounced duplicate scan.
    const [dupCandidates, setDupCandidates] = useState([]);
    const [scanning, setScanning] = useState(false);

    // Iter B (2026-10-09) — item-level selection. When the PO exposes
    // recurring components, the operator picks items per AMC and the amount
    // is derived from the selection. Blank-rated items stay unselected until
    // the operator explicitly enters a rate or marks "Included — ₹0".
    const components = preview?.amc_contract?.recurring_components || [];
    const hasComponents = components.length > 0;
    const [selection, setSelection] = useState({});
    const getSel = (c) => selection[c.component_key] || {
        included: (!!c.amc_rate_available || !!c.complimentary) && !c.allocated_to,
        rate: c.amc_rate_available ? String(c.amount) : "",
        includedZero: !!c.complimentary,
    };
    const setSel = (c, patch) => setSelection((prev) => ({
        ...prev,
        [c.component_key]: { ...getSel(c), ...patch },
    }));
    const selectedItems = components.filter((c) => !c.allocated_to && getSel(c).included);
    const derivedAmount = Math.round(selectedItems.reduce((sum, c) => {
        const s = getSel(c);
        return sum + (s.includedZero ? 0 : (Number(s.rate) || 0));
    }, 0) * 100) / 100;
    const blankRateSelected = selectedItems.filter((c) => {
        const s = getSel(c);
        return !s.includedZero && !(Number(s.rate) > 0);
    });

    const amountNum = hasComponents ? derivedAmount : (Number(form.amount_excl_gst) || 0);

    // Iter E (2026-10-14) — selection-driven subscription-end derivation.
    // The backend annotates every recurring component with its OWN
    // subscription end (label-matched to active non-AMC invoices). The
    // suggested dates follow the SELECTED items; conflicting active records
    // force explicit entry (never a silent "latest wins").
    const [datesTouched, setDatesTouched] = useState(false);
    const selConflicts = selectedItems.filter((c) => (c.subscription_end_conflicts || []).length > 0);
    const selEnds = [...new Set(selectedItems.map((c) => c.subscription_end_date).filter(Boolean))];
    const _nextDay = (iso) => { const d = new Date(iso + "T00:00:00Z"); d.setUTCDate(d.getUTCDate() + 1); return d.toISOString().slice(0, 10); };
    useEffect(() => {
        if (!hasComponents || datesTouched) return;
        if (selConflicts.length || selEnds.length > 1) {
            setForm((f) => ({ ...f, start_date: "", end_date: "" }));
        } else if (selEnds.length === 1) {
            const start = _nextDay(selEnds[0]);
            setForm((f) => ({ ...f, start_date: start, end_date: annualPeriodEndIso(start) }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selection, datesTouched, hasComponents]);

    /* eslint-disable react-hooks/set-state-in-effect -- debounced API fetch */
    useEffect(() => {
        if (!po?.client_id || amountNum <= 0 || !form.start_date || !form.end_date) {
            setDupCandidates([]); return;
        }
        if (form.end_date <= form.start_date) { setDupCandidates([]); return; }
        let cancelled = false;
        const t = setTimeout(async () => {
            setScanning(true);
            try {
                const r = await api.post(`/billing/amc/scan-duplicates`, {
                    client_id: po.client_id,
                    annual_amount_excl_gst: amountNum,
                    start_date: form.start_date,
                    end_date: form.end_date,
                    kind,
                });
                if (!cancelled) setDupCandidates(r.data?.candidates || []);
            } catch (e) {
                if (!cancelled) setDupCandidates([]);
            } finally {
                if (!cancelled) setScanning(false);
            }
        }, 450);
        return () => { cancelled = true; clearTimeout(t); };
    }, [po?.client_id, amountNum, form.start_date, form.end_date]);
    /* eslint-enable react-hooks/set-state-in-effect */
    // Iter 166b — period-aware pool accounting: when the preview computed an
    // overlap-aware allocation for the suggested period, renewals
    // (non-overlapping subsequent periods) must not double-count the pool.
    // The backend create-time check is authoritative on the submitted dates;
    // this advisory uses the suggested period's overlap.
    const overlappingAllocated = preview?.cap_check?.overlapping_allocated_excl_gst;
    const allocatedBase = overlappingAllocated ?? (pool.amc_allocated_excl_gst || 0);
    const allocatedAfter = allocatedBase + amountNum;
    const cap = pool.amc_contract_excl_gst || 0;
    const wouldOverAllocate = amountNum > 0 && allocatedAfter > cap + 0.01;
    const availableNow = Math.max(0, cap - allocatedBase);
    const needsPcdOverride = !!pcGate.override_required;
    const needsImplOverride = !!implGate.early_activation_requires_override;

    const isDirty = (
        !!form.amount_excl_gst || !!form.notes || Object.keys(selection).length > 0
        || !!form.project_completion_override_note
        || !!form.early_activation_override_note
        || !!form.over_allocation_override_note
    );

    let blockedReason = null;
    if (!form.amc_name.trim()) blockedReason = "AMC name is required";
    else if (hasComponents && selectedItems.length === 0) blockedReason = "Select at least one item for this AMC";
    else if (hasComponents && blankRateSelected.length > 0) blockedReason = `Set an AMC rate or mark "Included — ₹0" for: ${blankRateSelected[0].label}`;
    else if (hasComponents && derivedAmount <= 0) blockedReason = "Selected items total ₹0 — an AMC needs at least one charged item";
    else if (!hasComponents && (!form.amount_excl_gst || amountNum <= 0)) blockedReason = "Enter an AMC amount greater than ₹0";
    else if (!form.start_date) blockedReason = "Start date is required";
    else if (!form.end_date) blockedReason = "End date is required";
    else if (form.end_date <= form.start_date) blockedReason = "End date must be after start date";
    else if (!form.billing_frequency) blockedReason = "Pick a billing frequency";
    else if (needsPcdOverride && !form.project_completion_override_note.trim()) {
        blockedReason = "Project completion not recorded — provide override note";
    } else if (needsImplOverride && !form.early_activation_override_note.trim()) {
        blockedReason = "Implementation balance pending — provide early-activation note";
    } else if (wouldOverAllocate && !form.over_allocation_override_note.trim()) {
        blockedReason = `Over-allocation by ₹${(allocatedAfter - cap).toLocaleString("en-IN", { maximumFractionDigits: 2 })} — provide override note`;
    } else if (dupCandidates.length > 0 && !form.duplicate_override_note.trim()) {
        blockedReason = `${dupCandidates.length} possible duplicate AMC${dupCandidates.length === 1 ? "" : "s"} found — provide duplicate-override note`;
    }

    const submit = async (e) => {
        e?.preventDefault?.();
        if (blockedReason) {
            toast.error(blockedReason);
            return;
        }
        setBusy(true);
        try {
            const payload = {
                po_id: po.id,
                amc_name: form.amc_name.trim(),
                amount_excl_gst: amountNum,
                gst_percent: Number(form.gst_percent),
                start_date: form.start_date,
                end_date: form.end_date,
                billing_frequency: form.billing_frequency,
                notes: form.notes.trim(),
                kind,
            };
            if (hasComponents) {
                payload.selected_components = selectedItems.map((c) => {
                    const s = getSel(c);
                    return {
                        li_id: c.li_id,
                        component_code: c.component_code || null,
                        source: c.source,
                        rate: s.includedZero ? 0 : Number(s.rate),
                        included_no_charge: !!s.includedZero,
                    };
                });
            }
            if (form.billing_day) payload.billing_day = Number(form.billing_day);
            if (needsPcdOverride) payload.project_completion_override_note = form.project_completion_override_note.trim();
            if (needsImplOverride) payload.early_activation_override_note = form.early_activation_override_note.trim();
            if (wouldOverAllocate) payload.over_allocation_override_note = form.over_allocation_override_note.trim();
            if (dupCandidates.length > 0) payload.duplicate_override_note = form.duplicate_override_note.trim();

            await api.post(`/billing/amc/create-from-po`, payload);
            toast.success(isSub ? "Subscription schedule activated" : "AMC schedule activated");
            onOpenChange(false);
            onCreated?.();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    return (
        <FormDialog
            open={open}
            onOpenChange={onOpenChange}
            isDirty={isDirty}
            contentClassName="max-w-2xl"
            testId={isSub ? "create-subscription-dialog" : "create-amc-dialog"}
            title={isSub ? "Activate Subscription Schedule" : "Activate AMC Schedule"}
            description={isSub
                ? "Create one Annual Cost subscription against this PO's Annual Cost pool. Instalments are billed on calendar months and reconcile exactly to the annual amount."
                : "Create one AMC schedule against this PO's AMC pool. You can create additional schedules later, as long as the total stays within the pool."}
        >
            <form onSubmit={submit} className="space-y-4 pt-2">
                {/* Pool summary header */}
                <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 grid grid-cols-3 gap-3 text-xs">
                    <div>
                        <div className="reedos-eyebrow text-[9px]">Contract Value</div>
                        <div className="font-bold text-slate-900 mt-0.5">
                            {formatINR(pool.amc_contract_excl_gst, po?.currency)}
                        </div>
                    </div>
                    <div>
                        <div className="reedos-eyebrow text-[9px]">Already Allocated</div>
                        <div className="font-bold text-amber-700 mt-0.5">
                            {formatINR(pool.amc_allocated_excl_gst, po?.currency)}
                        </div>
                    </div>
                    <div>
                        <div className="reedos-eyebrow text-[9px]">Available to Allocate</div>
                        <div className="font-bold text-emerald-700 mt-0.5">
                            {formatINR(availableNow, po?.currency)}
                        </div>
                    </div>
                </div>

                {hasComponents && (
                    <div className="space-y-1.5" data-testid="amc-item-selection">
                        <Label>{isSub ? "Annual Cost items covered" : "Items covered by this AMC"}</Label>
                        <p className="text-[11px] text-slate-500">
                            Fetched from the PO — {isSub ? "Annual Cost" : "AMC"} rates are pre-filled where available (setup / one-time prices are never used).
                            Unselected items stay available for another {isSub ? "subscription" : "AMC"} on this PO.
                        </p>
                        <div className="border border-slate-200 rounded-md divide-y divide-slate-100 max-h-64 overflow-y-auto">
                            {components.map((c, i) => {
                                const s = getSel(c);
                                const allocated = c.allocated_to;
                                return (
                                    <div key={c.component_key} className="px-3 py-2 flex items-center gap-3 text-sm" data-testid={`amc-item-row-${i}`}>
                                        <input
                                            type="checkbox"
                                            checked={!!s.included && !allocated}
                                            disabled={!!allocated}
                                            onChange={(e) => setSel(c, { included: e.target.checked })}
                                            className="h-4 w-4 accent-slate-800"
                                            data-testid={`amc-item-include-${i}`}
                                        />
                                        <div className="flex-1 min-w-0">
                                            <div className="truncate text-slate-800">{c.label}</div>
                                            <div className="text-[10px] text-slate-500">
                                                {c.billing_frequency}
                                                {c.subscription_end_date && <span className="text-slate-500"> · sub ends {c.subscription_end_date}</span>}
                                                {(c.subscription_end_conflicts || []).length > 0 && <span className="text-rose-600"> · conflicting subscription records</span>}
                                                {allocated && <span className="text-amber-700"> · Allocated to {allocated.amc_name || allocated.schedule_id}</span>}
                                                {!allocated && !c.amc_rate_available && <span className="text-rose-600"> · {c.complimentary ? "Complimentary" : isSub ? "No Annual Cost on PO" : "No AMC rate on PO"}</span>}
                                            </div>
                                        </div>
                                        {!allocated && s.included && (
                                            <div className="flex items-center gap-2">
                                                <Input
                                                    type="text" inputMode="decimal"
                                                    value={s.includedZero ? "0" : s.rate}
                                                    disabled={s.includedZero}
                                                    onChange={(e) => setSel(c, { rate: e.target.value.replace(/[^\d.]/g, "") })}
                                                    className="h-8 w-28 text-sm text-right font-mono"
                                                    placeholder="Annual rate"
                                                    data-testid={`amc-item-rate-${i}`}
                                                />
                                                <label className="flex items-center gap-1 text-[10px] text-slate-600 whitespace-nowrap cursor-pointer">
                                                    <input
                                                        type="checkbox"
                                                        checked={!!s.includedZero}
                                                        onChange={(e) => setSel(c, { includedZero: e.target.checked })}
                                                        className="h-3.5 w-3.5 accent-emerald-700"
                                                        data-testid={`amc-item-included-zero-${i}`}
                                                    />
                                                    Included — ₹0
                                                </label>
                                            </div>
                                        )}
                                        {allocated && <span className="text-[10px] text-slate-400" data-testid={`amc-item-allocated-${i}`}>Allocated</span>}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                <div className="space-y-1.5">
                    <Label htmlFor="amc-name">Schedule name</Label>
                    <Input
                        id="amc-name" value={form.amc_name}
                        onChange={(e) => setForm({ ...form, amc_name: e.target.value })}
                        placeholder="e.g. AMC FY26 — Schedule A"
                        data-testid="amc-name-input" required maxLength={200}
                    />
                </div>

                <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                        <Label htmlFor="amc-amount">{isSub ? "Annual amount (excl GST)" : "AMC Amount (excl GST)"}</Label>
                        {hasComponents ? (
                            <div
                                className="h-10 flex items-center px-3 rounded-md border border-slate-200 bg-slate-50 font-mono text-sm"
                                data-testid="amc-amount-derived"
                            >
                                {formatINR(derivedAmount, po?.currency)}
                                <span className="text-[10px] text-slate-500 ml-2 font-sans">
                                    derived from {selectedItems.length} selected item{selectedItems.length === 1 ? "" : "s"}
                                </span>
                            </div>
                        ) : (
                        <Input
                            id="amc-amount" type="number" min="0" step="0.01"
                            value={form.amount_excl_gst}
                            onChange={(e) => setForm({ ...form, amount_excl_gst: e.target.value })}
                            placeholder="200000"
                            data-testid="amc-amount-input" required
                        />
                        )}
                        {amountNum > 0 && (
                            <p
                                className={`text-[11px] ${wouldOverAllocate ? "text-rose-700" : "text-slate-500"}`}
                                data-testid="amc-amount-allocation-hint"
                            >
                                After: ₹{allocatedAfter.toLocaleString("en-IN", { maximumFractionDigits: 2 })} of ₹{cap.toLocaleString("en-IN", { maximumFractionDigits: 2 })}
                                {wouldOverAllocate && ` · over by ₹${(allocatedAfter - cap).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`}
                            </p>
                        )}
                    </div>
                    <div className="space-y-1.5">
                        <Label htmlFor="amc-gst">GST %</Label>
                        <select
                            id="amc-gst"
                            value={form.gst_percent}
                            onChange={(e) => setForm({ ...form, gst_percent: e.target.value })}
                            className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-300"
                            data-testid="amc-gst-select"
                        >
                            {ALLOWED_GST_RATES.map((r) => (
                                <option key={r} value={String(r)}>{r}%</option>
                            ))}
                        </select>
                        <p className="text-[11px] text-slate-500">Default inherited from PO weighted GST.</p>
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                        <Label htmlFor="amc-start">Start date</Label>
                        <Input
                            id="amc-start" type="date" value={form.start_date}
                            onChange={(e) => { setDatesTouched(true); setForm({ ...form, start_date: e.target.value }); }}
                            data-testid="amc-start-input" required
                        />
                    </div>
                    <div className="space-y-1.5">
                        <Label htmlFor="amc-end">End date</Label>
                        <Input
                            id="amc-end" type="date" value={form.end_date}
                            onChange={(e) => { setDatesTouched(true); setForm({ ...form, end_date: e.target.value }); }}
                            data-testid="amc-end-input" required
                        />
                    </div>
                </div>
                {selConflicts.length > 0 && (
                    <p className="text-[11px] text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-2 py-1.5" data-testid="amc-dates-conflict">
                        Conflicting subscription records for {selConflicts.map((c) => c.label).join(", ")} — multiple active invoices disagree on the end date. Enter the AMC dates explicitly.
                    </p>
                )}
                {selConflicts.length === 0 && selEnds.length > 1 && (
                    <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5" data-testid="amc-dates-varied">
                        Selected items have different subscription end dates — one AMC covers one service period. Remove items or enter the dates explicitly.
                    </p>
                )}
                {preview?.suggested_dates_source?.kind === "po_line_dates" && (
                    <p className="text-[11px] text-sky-800 bg-sky-50 border border-sky-200 rounded-md px-2 py-1.5" data-testid="amc-dates-source">
                        Dates suggested from the service period on this PO&apos;s Annual Cost lines — starts{" "}
                        <span className="font-mono">{formatDate(preview.suggested_dates_source.service_start_date)}</span>. Check the end date against the agreed coverage period.
                    </p>
                )}
                {preview?.suggested_dates_source && preview.suggested_dates_source.kind !== "po_line_dates" && (
                    <p className="text-[11px] text-sky-800 bg-sky-50 border border-sky-200 rounded-md px-2 py-1.5" data-testid="amc-dates-source">
                        Dates suggested from the current subscription — invoice{" "}
                        <span className="font-mono">{preview.suggested_dates_source.source_invoice_number}</span>
                        {" "}ends on <span className="font-mono">{preview.suggested_dates_source.subscription_end_date}</span>. The {isSub ? "subscription" : "AMC"} starts the next day.
                    </p>
                )}

                <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                        <Label htmlFor="amc-freq">Billing frequency</Label>
                        <select
                            id="amc-freq"
                            value={form.billing_frequency}
                            onChange={(e) => setForm({ ...form, billing_frequency: e.target.value })}
                            className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-300"
                            data-testid="amc-freq-select"
                        >
                            {ALLOWED_FREQUENCIES.map((f) => (
                                <option key={f} value={f}>
                                    {f}{suggestedFrequencies.includes(f) ? "" : " (not in PO)"}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="space-y-1.5">
                        <Label htmlFor="amc-billing-day">Billing day (optional)</Label>
                        <Input
                            id="amc-billing-day" type="number" min="1" max="28"
                            value={form.billing_day}
                            onChange={(e) => setForm({ ...form, billing_day: e.target.value })}
                            placeholder="e.g. 5"
                            data-testid="amc-billing-day-input"
                        />
                    </div>
                </div>

                <div className="space-y-1.5">
                    <Label htmlFor="amc-notes">Notes (optional)</Label>
                    <Textarea
                        id="amc-notes" rows={2}
                        value={form.notes}
                        onChange={(e) => setForm({ ...form, notes: e.target.value })}
                        placeholder={isSub ? "e.g. Subscription commenced on …" : "e.g. AMC commenced post UAT sign-off on …"}
                        data-testid="amc-notes-input" maxLength={2000}
                    />
                </div>

                {/* G2 — Project completion override */}
                {needsPcdOverride && (
                    <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5 space-y-1.5" data-testid="pcd-override-panel">
                        <div className="flex items-center gap-1.5">
                            <WarningCircle size={14} className="text-amber-700" weight="fill" />
                            <div className="reedos-eyebrow text-[10px] text-amber-900">Project completion not recorded</div>
                        </div>
                        <p className="text-[11px] text-amber-800">
                            The Project Completion Date has not been recorded for this PO. Super Admin may activate AMC ahead of completion by providing a written reason.
                        </p>
                        <Textarea
                            rows={2}
                            value={form.project_completion_override_note}
                            onChange={(e) => setForm({ ...form, project_completion_override_note: e.target.value })}
                            placeholder="Reason for activating AMC before recording project completion…"
                            data-testid="pcd-override-input"
                            maxLength={2000}
                        />
                    </div>
                )}

                {/* G3 — Implementation early-activation override */}
                {needsImplOverride && (
                    <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5 space-y-1.5" data-testid="impl-override-panel">
                        <div className="flex items-center gap-1.5">
                            <WarningCircle size={14} className="text-amber-700" weight="fill" />
                            <div className="reedos-eyebrow text-[10px] text-amber-900">
                                Implementation balance pending: {formatINR(implGate.balance_to_invoice, po?.currency)}
                            </div>
                        </div>
                        <p className="text-[11px] text-amber-800">
                            Implementation invoicing has not been completed. Super Admin may activate AMC early with a written reason.
                        </p>
                        <Textarea
                            rows={2}
                            value={form.early_activation_override_note}
                            onChange={(e) => setForm({ ...form, early_activation_override_note: e.target.value })}
                            placeholder="Reason for activating AMC before completing Implementation invoicing…"
                            data-testid="impl-override-input"
                            maxLength={2000}
                        />
                    </div>
                )}

                {/* D3 — Over-allocation override */}
                {wouldOverAllocate && (
                    <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2.5 space-y-1.5" data-testid="overalloc-override-panel">
                        <div className="flex items-center gap-1.5">
                            <WarningCircle size={14} className="text-rose-700" weight="fill" />
                            <div className="reedos-eyebrow text-[10px] text-rose-900">
                                Over-allocation: {formatINR(allocatedAfter - cap, po?.currency)} above pool cap
                            </div>
                        </div>
                        <p className="text-[11px] text-rose-800">
                            Total allocation across all linked schedules would exceed the PO&apos;s AMC pool. Super Admin override with mandatory reason and audit trail.
                        </p>
                        <Textarea
                            rows={2}
                            value={form.over_allocation_override_note}
                            onChange={(e) => setForm({ ...form, over_allocation_override_note: e.target.value })}
                            placeholder="Reason for over-allocation (e.g. ECO-2026-001 change order)…"
                            data-testid="overalloc-override-input"
                            maxLength={2000}
                        />
                    </div>
                )}

                {/* DEDUP-AMC-001 — Possible duplicate warning panel.
                    Renders whenever the live scan returns >=1 candidates.
                    Super-admin can proceed with a one-line override note,
                    audit-logged on the new schedule. */}
                {dupCandidates.length > 0 && (
                    <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2.5 space-y-1.5" data-testid="duplicate-warning-panel">
                        <div className="flex items-center gap-1.5">
                            <WarningCircle size={14} className="text-rose-700" weight="fill" />
                            <div className="reedos-eyebrow text-[10px] text-rose-900">
                                {dupCandidates.length} possible duplicate AMC{dupCandidates.length === 1 ? "" : "s"} detected
                            </div>
                        </div>
                        <ul className="text-[11px] text-rose-900 space-y-0.5 max-h-32 overflow-y-auto pl-1">
                            {dupCandidates.slice(0, 6).map((c) => (
                                <li key={c.schedule_id} data-testid={`dup-candidate-${c.schedule_id}`} className="flex items-start gap-1.5">
                                    <span className="font-mono text-[10px] text-rose-700">·</span>
                                    <div className="flex-1 min-w-0">
                                        <span className="font-semibold">{c.amc_name}</span>
                                        <span className="text-rose-700"> · {c.client_name}</span>
                                        <span className="text-rose-700"> · {c.billing_frequency} · {formatINR(c.annualised_excl_gst, po?.currency)}/yr</span>
                                        {!c.same_client && c.same_gstin && (
                                            <span className="ml-1 inline-block rounded bg-rose-100 px-1 py-0 text-[9px] font-bold">same GSTIN, different client</span>
                                        )}
                                    </div>
                                </li>
                            ))}
                            {dupCandidates.length > 6 && (
                                <li className="text-rose-700 italic pl-3">… and {dupCandidates.length - 6} more</li>
                            )}
                        </ul>
                        <p className="text-[11px] text-rose-800">
                            Super Admin may proceed if this is a legitimate separate contract. Provide a written reason for audit.
                        </p>
                        <Textarea
                            rows={2}
                            value={form.duplicate_override_note}
                            onChange={(e) => setForm({ ...form, duplicate_override_note: e.target.value })}
                            placeholder="Reason for creating despite possible duplicate (e.g. ECO-2026-002 separate subsidiary contract)…"
                            data-testid="duplicate-override-input"
                            maxLength={2000}
                        />
                    </div>
                )}
                {scanning && (
                    <p className="text-[10px] text-slate-500" data-testid="duplicate-scan-progress">Checking for duplicates…</p>
                )}

                {blockedReason && (
                    <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-[11px] text-slate-600" data-testid="amc-blocked-reason">
                        {blockedReason}
                    </div>
                )}

                <div className="flex justify-end gap-2 pt-1">
                    <FormDialogCancel testId="amc-cancel-btn" />
                    <Button
                        type="submit"
                        disabled={busy || !!blockedReason}
                        data-testid="amc-submit-btn"
                    >
                        {busy ? "Activating…" : "Activate Schedule"}
                    </Button>
                </div>
            </form>
        </FormDialog>
    );
}

/* ------------------------------------------------------------------------ */
/*  LinkedSchedulesList                                                    */
/* ------------------------------------------------------------------------ */

export function LinkedSchedulesList({ schedules, currency, emptyText = "No AMC schedules linked yet" }) {
    if (!schedules?.length) {
        return (
            <div className="rounded-md border border-dashed border-slate-200 bg-slate-50 px-3 py-4 text-center" data-testid="linked-schedules-empty">
                <ListChecks size={16} className="mx-auto text-slate-400" />
                <p className="text-xs text-slate-500 mt-1">{emptyText}</p>
            </div>
        );
    }
    return (
        <div className="overflow-hidden rounded-md border border-slate-200" data-testid="linked-schedules-list">
            <table className="w-full text-xs">
                <thead className="bg-slate-50 border-b border-slate-200">
                    <tr className="text-left">
                        <th className="px-3 py-2 reedos-eyebrow">AMC Name</th>
                        <th className="px-3 py-2 reedos-eyebrow">Frequency</th>
                        <th className="px-3 py-2 reedos-eyebrow">Period</th>
                        <th className="px-3 py-2 reedos-eyebrow text-right">Amount</th>
                        <th className="px-3 py-2 reedos-eyebrow text-right">Invoiced</th>
                        <th className="px-3 py-2 reedos-eyebrow">Status</th>
                        <th className="px-3 py-2 reedos-eyebrow"></th>
                    </tr>
                </thead>
                <tbody>
                    {schedules.map((s) => (
                        <tr
                            key={s.id}
                            className="border-t border-slate-100 hover:bg-slate-50"
                            data-testid={`linked-sched-row-${s.id}`}
                        >
                            <td className="px-3 py-2">
                                <div className="font-medium text-slate-900">{s.amc_name || "AMC"}</div>
                                <div className="text-[10px] text-slate-500">{s.id}</div>
                            </td>
                            <td className="px-3 py-2 text-slate-700">{s.billing_frequency}</td>
                            <td className="px-3 py-2 text-slate-700">
                                {formatDate(s.start_date)} → {formatDate(s.end_date)}
                            </td>
                            <td className="px-3 py-2 text-right font-semibold text-slate-900">
                                {formatINR(s.total_amount, currency)}
                            </td>
                            <td className="px-3 py-2 text-right text-slate-700">
                                {formatINR(s.schedule_invoiced_incl_gst, currency)}
                            </td>
                            <td className="px-3 py-2">
                                <StatusBadge status={s.status} />
                                {s.over_allocation_override && (
                                    <span
                                        className="ml-1 inline-block rounded bg-rose-50 text-rose-700 border border-rose-200 px-1.5 py-0.5 text-[9px] font-bold"
                                        title={s.over_allocation_override.note}
                                    >
                                        Override
                                    </span>
                                )}
                            </td>
                            <td className="px-3 py-2">
                                <Link
                                    to={`/amc/${s.id}`}
                                    className="text-xs text-slate-700 hover:text-slate-900 inline-flex items-center gap-0.5"
                                    data-testid={`linked-sched-open-${s.id}`}
                                >
                                    Open <ArrowRight size={12} />
                                </Link>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

/* ------------------------------------------------------------------------ */
/*  Main AmcPoolPanel                                                       */
/* ------------------------------------------------------------------------ */

export default function AmcPoolPanel({ po, onChanged }) {
    const bs = po?.billing_status;
    const [preview, setPreview] = useState(null);
    const [previewError, setPreviewError] = useState(null);

    const hasAmcValue = !!bs?.has_amc_value;
    const linkedSchedules = bs?.linked_amc_schedules || [];
    const pcRecorded = !!bs?.project_completion_recorded;
    const activationUnlocked = !!bs?.amc_activation_unlocked;
    const gateReasons = bs?.amc_activation_gate_reasons || [];
    const available = bs?.amc_available_to_allocate_amount ?? 0;

    // Lazy-fetch the creation preview so the dialog can render the
    // pool_summary + frequency hints + gate evaluation without a second
    // call. Refetched whenever the PO changes.
    useEffect(() => {
        if (!po?.id || !hasAmcValue) return;
        let cancelled = false;
        (async () => {
            try {
                const r = await api.get(`/billing/amc/po/${po.id}/amc-creation-preview`);
                if (!cancelled) setPreview(r.data);
            } catch (err) {
                if (!cancelled) setPreviewError(formatApiError(err));
            }
        })();
        return () => { cancelled = true; };
    }, [po?.id, hasAmcValue, bs?.amc_allocated_amount, bs?.project_completion_date]);

    if (!hasAmcValue) return null;

    const cannotAllocateMore = available <= 0.01;
    const activateDisabled = !activationUnlocked || cannotAllocateMore;
    const activateDisabledReason = cannotAllocateMore
        ? "AMC pool fully allocated"
        : gateReasons.join(" · ");

    return (
        <div className="reedos-card p-5" data-testid="amc-pool-panel">
            <div className="flex items-center gap-2 mb-3">
                <Shield size={14} className="text-violet-700" weight="duotone" />
                <div className="reedos-eyebrow">AMC Pool</div>
                {bs?.amc_over_billed_amount > 0 && (
                    <span
                        className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-[10px] font-bold border bg-rose-50 text-rose-700 border-rose-200"
                        title="AMC cycle invoices exceed the contractual pool"
                        data-testid="amc-over-billed-badge"
                    >
                        Over-billed by {formatINR(bs.amc_over_billed_amount, po?.currency)}
                    </span>
                )}
                {activationUnlocked && (
                    <span
                        className="ml-auto inline-flex items-center gap-1 rounded px-2 py-0.5 text-[10px] font-bold border bg-emerald-50 text-emerald-800 border-emerald-200"
                        data-testid="amc-activation-ready-badge"
                    >
                        <CheckCircle size={10} weight="fill" /> Ready for AMC
                    </span>
                )}
            </div>

            {/* AMC pool utilisation bar — Invoiced (indigo) · Outstanding (amber) · Available (emerald)
                summing to Contract Value. Gives an at-a-glance read of commitment vs spend vs free. */}
            {bs.amc_po_total > 0 && (() => {
                const contract = bs.amc_po_total || 0;
                const invoiced = Math.max(0, Math.min(bs.amc_invoiced_amount || 0, contract));
                const allocated = Math.max(0, Math.min(bs.amc_allocated_amount || 0, contract));
                const outstanding = Math.max(0, allocated - invoiced);
                const available = Math.max(0, contract - allocated);
                const pct = (n) => `${(n / contract * 100).toFixed(2)}%`;
                return (
                    <div className="mb-3" data-testid="amc-pool-utilisation-bar">
                        <div className="flex h-2 w-full overflow-hidden rounded-full bg-slate-100">
                            {invoiced > 0 && (
                                <div
                                    className="bg-indigo-600"
                                    style={{ width: pct(invoiced) }}
                                    title={`Invoiced ${formatINR(invoiced, po?.currency)}`}
                                    data-testid="util-seg-invoiced"
                                />
                            )}
                            {outstanding > 0 && (
                                <div
                                    className="bg-amber-500"
                                    style={{ width: pct(outstanding) }}
                                    title={`Outstanding ${formatINR(outstanding, po?.currency)}`}
                                    data-testid="util-seg-outstanding"
                                />
                            )}
                            {available > 0 && (
                                <div
                                    className="bg-emerald-500"
                                    style={{ width: pct(available) }}
                                    title={`Available ${formatINR(available, po?.currency)}`}
                                    data-testid="util-seg-available"
                                />
                            )}
                        </div>
                        <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1 text-[10px] text-slate-600">
                            <span className="inline-flex items-center gap-1">
                                <span className="inline-block w-2 h-2 rounded-sm bg-indigo-600" />
                                Invoiced {pct(invoiced)}
                            </span>
                            <span className="inline-flex items-center gap-1">
                                <span className="inline-block w-2 h-2 rounded-sm bg-amber-500" />
                                Outstanding {pct(outstanding)}
                            </span>
                            <span className="inline-flex items-center gap-1">
                                <span className="inline-block w-2 h-2 rounded-sm bg-emerald-500" />
                                Available {pct(available)}
                            </span>
                        </div>
                    </div>
                );
            })()}

            {/* 6 tiles */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                <PoolTile
                    label="Contract Value"
                    value={bs.amc_po_total}
                    currency={po?.currency}
                    tone="slate"
                    testid="amc-pool-contract"
                    subtitle={`Excl GST: ${formatINR(bs.amc_po_excl_gst, po?.currency)}`}
                />
                <PoolTile
                    label="Allocated"
                    value={bs.amc_allocated_amount}
                    currency={po?.currency}
                    tone="amber"
                    testid="amc-pool-allocated"
                />
                <PoolTile
                    label="Available to Allocate"
                    value={bs.amc_available_to_allocate_amount}
                    currency={po?.currency}
                    tone="emerald"
                    testid="amc-pool-available"
                />
                <CountTile
                    label="Schedules"
                    count={bs.linked_amc_schedule_count || 0}
                    testid="amc-pool-count"
                />
                <PoolTile
                    label="Invoiced"
                    value={bs.amc_invoiced_amount}
                    currency={po?.currency}
                    tone="indigo"
                    testid="amc-pool-invoiced"
                />
                <PoolTile
                    label="Outstanding"
                    value={bs.amc_outstanding_amount}
                    currency={po?.currency}
                    tone={bs.amc_outstanding_amount > 0 ? "rose" : "emerald"}
                    testid="amc-pool-outstanding"
                />
            </div>

            {/* Project completion strip */}
            <div
                className="mt-4 rounded-md border border-slate-200 bg-slate-50 px-3 py-2.5 flex items-start gap-3"
                data-testid="amc-pool-project-completion"
            >
                <Calendar
                    size={16}
                    weight={pcRecorded ? "fill" : "regular"}
                    className={pcRecorded ? "text-emerald-700 mt-0.5" : "text-slate-400 mt-0.5"}
                />
                <div className="flex-1 min-w-0">
                    <div className="text-xs font-semibold text-slate-900">
                        Project Completion
                        {pcRecorded && (
                            <span className="ml-2 text-slate-500 font-normal">
                                {formatDate(bs.project_completion_date)}
                                {bs.project_completion_recorded_by?.email && (
                                    <> · by {bs.project_completion_recorded_by.email}</>
                                )}
                            </span>
                        )}
                    </div>
                    {pcRecorded ? (
                        <p className="text-[11px] text-slate-600 mt-0.5 line-clamp-2" data-testid="pcd-remarks-display">
                            {bs.project_completion_remarks || "No remarks recorded"}
                        </p>
                    ) : (
                        <p className="text-[11px] text-slate-500 mt-0.5">
                            Record project completion to unlock AMC activation.
                        </p>
                    )}
                </div>
                <RecordProjectCompletionDialog po={po} onSaved={onChanged} />
            </div>

            {/* Linked schedules + Activate CTA */}
            <div className="mt-4">
                <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-1.5">
                        <Receipt size={12} className="text-slate-600" />
                        <div className="reedos-eyebrow text-[10px]">Linked AMC Schedules</div>
                        {linkedSchedules.length > 0 && (
                            <Link
                                to={`/amc?po_id=${po.id}`}
                                className="text-[10px] text-slate-500 hover:text-slate-900 underline-offset-2 hover:underline ml-2"
                                data-testid="amc-pool-view-all-link"
                            >
                                View all in AMC list →
                            </Link>
                        )}
                    </div>
                    <CreateAmcFromPoDialog
                        po={po}
                        preview={preview}
                        onCreated={onChanged}
                        disabled={activateDisabled}
                        disabledReason={activateDisabledReason}
                    />
                </div>
                <LinkedSchedulesList schedules={linkedSchedules} currency={po?.currency} />
                {!activationUnlocked && gateReasons.length > 0 && (
                    <p className="text-[11px] text-slate-500 mt-2" data-testid="amc-gate-reasons">
                        AMC activation gates: {gateReasons.join(" · ")}
                    </p>
                )}
                {previewError && (
                    <p className="text-[11px] text-rose-600 mt-2">{previewError}</p>
                )}
            </div>
        </div>
    );
}
